import { type MutableRefObject, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { type TaskInsightsResponse } from '../../lib/api';
import { computeWeeklyHabitHealth, getHabitHealth as getHabitHealthFromInsights } from '../../lib/habitHealth';
import { usePostLoginLanguage } from '../../i18n/postLoginLanguage';
import { resolveEmotionCopy } from '../../config/emotionMessages';
import type { WeeklyWrappedPayload, WeeklyWrappedSection } from '../../lib/weeklyWrapped';

const ANIMATION_DELAY = 80;
const PILLAR_ICONS: Record<string, string> = {
  Body: '🫀',
  Mind: '🧠',
  Soul: '🏵️',
};

function normalizePillarCode(value: unknown): keyof typeof PILLAR_ICONS | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    const numeric = Number(value);
    if (numeric === 1) return 'Body';
    if (numeric === 2) return 'Mind';
    if (numeric === 3) return 'Soul';
  }

  const text = value.toString().trim();
  if (!text) {
    return null;
  }

  const upper = text.toUpperCase();
  if (upper === 'BODY' || upper === 'CUERPO') return 'Body';
  if (upper === 'MIND' || upper === 'MENTE') return 'Mind';
  if (upper === 'SOUL' || upper === 'ALMA') return 'Soul';

  const numericText = Number(text);
  if (Number.isFinite(numericText)) {
    return normalizePillarCode(numericText);
  }

  const match = text.match(/(\d+)/);
  if (match?.[1]) {
    return normalizePillarCode(Number.parseInt(match[1], 10));
  }

  return null;
}

const HABIT_ICON_SETS: Record<string, string[]> = {
  Body: ['🏃‍♂️', '💪', '🥗', '🔥'],
  Mind: ['📚', '🎯', '✍️', '🧩'],
  Soul: ['🌱', '🧘', '✨', '🌙'],
};

const PILLAR_GRADIENTS: Record<string, string> = {
  Mind: 'from-sky-400/30 via-sky-500/10 to-indigo-500/20',
  Body: 'from-emerald-400/30 via-lime-400/10 to-cyan-500/20',
  Soul: 'from-fuchsia-400/30 via-rose-400/10 to-amber-400/20',
};

const PILLAR_TEXT_TONES: Record<string, string> = {
  Mind: 'text-sky-50',
  Body: 'text-emerald-50',
  Soul: 'text-fuchsia-50',
};

type SectionBadge = {
  id: string;
  label: string;
  icon?: string;
  dotColor?: string;
  active?: boolean;
  dotOnly?: boolean;
  size?: 'default' | 'large';
};

type WeeklyWrappedModalProps = {
  payload: WeeklyWrappedPayload;
  onDismiss?: () => void;
  onComplete?: () => void;
  onViewRewards?: () => void;
  onClose?: () => void;
};

const GRADIENT_RING_CLASSES = [
  'from-emerald-400/40 via-cyan-400/10 to-indigo-500/40',
  'from-amber-400/40 via-rose-400/20 to-fuchsia-500/40',
  'from-sky-400/40 via-emerald-400/20 to-violet-500/40',
];

const ENERGY_METRIC_BY_PILLAR: Record<string, { label: 'HP' | 'FOCUS' | 'MOOD'; color: string }> = {
  Body: { label: 'HP', color: '#22c55e' },
  Mind: { label: 'FOCUS', color: '#38bdf8' },
  Soul: { label: 'MOOD', color: '#e879f9' },
};

const ENERGY_GRADIENT_BY_METRIC: Record<'HP' | 'MOOD' | 'FOCUS', string> = {
  HP: 'from-cyan-200 via-sky-300 to-blue-400',
  MOOD: 'from-rose-200 via-pink-300 to-fuchsia-400',
  FOCUS: 'from-indigo-200 via-violet-300 to-purple-400',
};

export function WeeklyWrappedModal({ payload, onDismiss, onComplete, onViewRewards, onClose }: WeeklyWrappedModalProps) {
  const dismissHandler = onDismiss ?? onClose ?? (() => {});
  const completeHandler = onComplete ?? onClose ?? (() => {});
  const viewRewardsHandler = onViewRewards ?? completeHandler;
  const { language, t } = usePostLoginLanguage();
  const [entered, setEntered] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    console.info('[weekly-wrapped] rendering modal with payload', {
      dataSource: payload.dataSource,
      variant: payload.variant,
      weekRange: payload.weekRange,
      summary: payload.summary,
      sections: payload.sections.map((section) => section.key),
    });
  }, [payload]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPosition = body.style.position;
    const previousBodyWidth = body.style.width;
    const previousBodyTop = body.style.top;
    const scrollY = window.scrollY;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.width = '100%';
    body.style.top = `-${scrollY}px`;

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.position = previousBodyPosition;
      body.style.width = previousBodyWidth;
      body.style.top = previousBodyTop;
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let bestMatch: { index: number; ratio: number } | null = null;

        for (const entry of entries) {
          const indexAttr = entry.target.getAttribute('data-index');
          if (!indexAttr) continue;
          const index = Number(indexAttr);
          const ratio = entry.intersectionRatio;

          if (!bestMatch || ratio > bestMatch.ratio) {
            bestMatch = { index, ratio };
          }
        }

        if (bestMatch) {
          setActiveIndex(bestMatch.index);
        }
      },
      {
        root: containerRef.current,
        threshold: [0.4, 0.55, 0.7],
      },
    );

    sectionRefs.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const sectionsByKey = useMemo(() => {
    const map: Record<string, WeeklyWrappedSection> = {};
    for (const section of payload.sections) {
      map[section.key] = section;
    }
    return map;
  }, [payload.sections]);

  const achievementsMetrics = extractSummaryMetrics(sectionsByKey.achievements?.body);

  const completions = payload.summary?.completions ?? achievementsMetrics?.completions ?? 0;
  const xpTotal = payload.summary?.xpTotal ?? achievementsMetrics?.xpTotal ?? 0;
  const hasAchievementStats = completions > 0 || xpTotal > 0 || Boolean(achievementsMetrics);

  const habitsItems = (sectionsByKey.habits?.items ?? [])
    .map((item, idx) => {
      const daysMatch = item.body?.match(/(\d+)\/7/);
      const daysActive = Number.isFinite(item.daysActive)
        ? item.daysActive
        : daysMatch
          ? Number.parseInt(daysMatch[1] ?? '0', 10)
          : undefined;
      const weeksActive = item.weeksActive;
      const weeksSample = item.weeksSample;
      const completionRate =
        typeof item.completionRate === 'number' || typeof item.completionRate === 'string'
          ? item.completionRate
          : undefined;
      const weeklyGoal =
        typeof item.weeklyGoal === 'number' || typeof item.weeklyGoal === 'string' ? item.weeklyGoal : undefined;
      return {
        ...item,
        icon: getHabitIcon(item.pillar, idx),
        daysActive,
        weeksActive,
        weeksSample,
        completionRate,
        weeklyGoal,
      };
    })
    .slice(0, 5);
  const topHabitTitle = habitsItems[0]?.title;
  const topHabitPillarIcon = habitsItems[0]?.pillar ? getPillarIcon(habitsItems[0]?.pillar) : null;
  const pillarDominant = payload.summary.pillarDominant;
  const emotionHighlight = payload.emotions;
  const weeklyEmotion = emotionHighlight.weekly ?? emotionHighlight.biweekly;
  const levelUp = payload.levelUp;
  const showLevelUp = levelUp?.happened;
  const habitsStartIndex = showLevelUp ? 2 : 1;
  const progressIndex = habitsStartIndex + 1;
  const emotionIndex = progressIndex + 1;
  const closingIndex = emotionIndex + 1;
  const referenceDate = useMemo(
    () => new Date(payload.lastActivityAt ?? payload.weekRange?.end ?? Date.now()),
    [payload.lastActivityAt, payload.weekRange?.end],
  );

  const summaryChips: SectionBadge[] = useMemo(
    () =>
      [
        {
          id: 'range',
          label: formatRange(payload.weekRange, language),
          active: true,
          icon: '📅',
        },
        weeklyEmotion
          ? {
              id: 'emotion',
              label: '',
              dotColor: weeklyEmotion.color,
              active: true,
              dotOnly: true,
            }
          : {
              id: 'emotion-empty',
              label: '',
              dotColor: '#94a3b8',
              active: false,
              dotOnly: true,
            },
        topHabitTitle || pillarDominant
          ? {
              id: 'streak',
              label: `${topHabitPillarIcon || getPillarIcon(pillarDominant) || '🫀'}`,
              icon: '🔥',
              active: true,
              size: 'large',
            }
          : {
              id: 'streak-empty',
              label: '🫀',
              icon: '🔥',
              active: false,
              size: 'large',
            },
      ] satisfies SectionBadge[],
    [payload.weekRange, topHabitPillarIcon, pillarDominant, topHabitTitle, weeklyEmotion],
  );

  // Fondo animado global aplicado al wrapper fijo que envuelve todas las slides del Weekly Wrapped (desktop y mobile usan este contenedor).
  return (
    <div
      className="ib-weekly-wrapped-modal fixed inset-0 isolate z-[9999] flex h-[100dvh] w-screen overflow-hidden bg-slate-950 weekly-wrapped-animated-bg"
      role="dialog"
      aria-modal
    >
      <div className="absolute inset-0 bg-slate-950" onClick={dismissHandler} aria-hidden />

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-16 left-6 h-64 w-64 animate-[spin_22s_linear_infinite] bg-[radial-gradient(circle_at_center,_rgba(99,179,237,0.18),_transparent_55%)] blur-2xl" />
        <div className="absolute bottom-6 right-10 h-80 w-80 animate-[spin_26s_linear_reverse_infinite] bg-[radial-gradient(circle_at_center,_rgba(236,72,153,0.18),_transparent_55%)] blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_20%_10%,rgba(110,231,183,0.16),transparent),radial-gradient(90%_80%_at_80%_20%,rgba(94,234,212,0.12),transparent),radial-gradient(80%_120%_at_40%_80%,rgba(192,132,252,0.14),transparent)]" />
      </div>

      <div className="relative z-10 flex h-[100dvh] w-full flex-col">
        <button
          type="button"
          onClick={dismissHandler}
          className="ib-weekly-wrapped-close absolute right-5 top-5 z-20 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-50 shadow-lg shadow-emerald-500/20 transition hover:-translate-y-0.5 hover:border-emerald-300/60 hover:bg-emerald-400/30 hover:text-slate-950"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 20px)' }}
        >
          {t('feedback.common.close')}
        </button>

        <div className="relative h-full w-full overflow-hidden">
          <div
            className="relative h-[100dvh] w-full snap-y snap-mandatory overflow-y-auto scroll-smooth"
            ref={containerRef}
          >
            <SectionBlock
              label={sectionsByKey.intro?.title ?? t('feedback.weeklyWrapped.intro.label')}
              headline={sectionsByKey.intro?.body ?? t('feedback.weeklyWrapped.intro.headline')}
              badges={summaryChips}
              description={
                hasAchievementStats
                  ? undefined
                  : sectionsByKey.achievements?.body ??
                    t('feedback.weeklyWrapped.intro.fallbackAchievements')
              }
              kicker={sectionsByKey.achievements?.accent}
              stats={{
                completions,
                xpTotal,
                pillar: pillarDominant,
                pillarStats: payload.summary.pillarDominantStats ?? null,
              }}
              entered={entered}
              index={0}
              active={activeIndex === 0}
              registerSectionRef={(el) => (sectionRefs.current[0] = el)}
              t={t}
            />

            {showLevelUp ? (
              <LevelUpBlock
                levelUp={levelUp}
                index={1}
                entered={entered}
                active={activeIndex === 1}
                registerSectionRef={(el) => (sectionRefs.current[1] = el)}
                t={t}
              />
            ) : null}

            <HabitsBlock
              title={sectionsByKey.habits?.title ?? t('feedback.weeklyWrapped.habits.title')}
              description={
                sectionsByKey.habits?.body ??
                t('feedback.weeklyWrapped.habits.description')
              }
              items={habitsItems.map((item, idx) => ({
                ...item,
                icon: item.icon || getHabitIcon(item.pillar, idx),
              }))}
              entered={entered}
              startIndex={habitsStartIndex}
              activeIndex={activeIndex}
              registerSectionRef={sectionRefs}
              referenceDate={referenceDate}
              t={t}
            />

            <ProgressBlock
              pillar={sectionsByKey.pillar}
              pillarDominant={pillarDominant}
              completions={completions}
              xpTotal={xpTotal}
              energyHighlight={payload.summary.energyHighlight}
              effortBalance={payload.summary.effortBalance}
              entered={entered}
              index={progressIndex}
              active={activeIndex === progressIndex}
              registerSectionRef={(el) => (sectionRefs.current[progressIndex] = el)}
              t={t}
            />

            <EmotionHighlightBlock
              emotionHighlight={emotionHighlight}
              entered={entered}
              index={emotionIndex}
              active={activeIndex === emotionIndex}
              registerSectionRef={(el) => (sectionRefs.current[emotionIndex] = el)}
              t={t}
              language={language}
            />

            <ClosingBlock
              message={sectionsByKey.closing?.body ?? t('feedback.weeklyWrapped.closing.message')}
              accent={sectionsByKey.closing?.accent ?? t('feedback.weeklyWrapped.closing.accent')}
              onComplete={completeHandler}
              onViewRewards={viewRewardsHandler}
              entered={entered}
              index={closingIndex}
              active={activeIndex === closingIndex}
              registerSectionRef={(el) => (sectionRefs.current[closingIndex] = el)}
              t={t}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

type SectionShellProps = {
  children: ReactNode;
  index: number;
  entered: boolean;
  active?: boolean;
  auraIndex?: number;
  registerSectionRef?: (el: HTMLDivElement | null) => void;
};

/**
 * Layout contract
 * - WeeklyWrappedModal locks the viewport height with a scroll-snap stack (100dvh).
 * - Each SectionShell is full-bleed (100vw x 100dvh) with overflow hidden and an internal content scroller.
 * - Safe areas are handled in the content container to avoid clipping on devices with notches.
 */
function SectionShell({ children, index, entered, active = false, auraIndex = 0, registerSectionRef }: SectionShellProps) {
  const delay = `${ANIMATION_DELAY * index}ms`;
  const auraClasses = GRADIENT_RING_CLASSES[auraIndex % GRADIENT_RING_CLASSES.length];
  const safeAreaStyle = {
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 32px)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
  };

  return (
    <section
      className="relative flex h-[100dvh] min-h-[100dvh] w-[100vw] flex-none snap-start overflow-hidden"
      aria-live="polite"
      data-index={index}
      ref={registerSectionRef}
    >
      <div className="absolute inset-0 bg-slate-950" aria-hidden />
      <div className={`pointer-events-none absolute inset-[-15%] bg-gradient-to-br opacity-80 blur-3xl ${auraClasses}`} aria-hidden />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_120%_at_30%_20%,rgba(16,185,129,0.14),transparent),radial-gradient(110%_110%_at_70%_80%,rgba(79,70,229,0.12),transparent)]" aria-hidden />

      <div
        className={`relative z-10 mx-auto flex h-full w-full max-w-5xl flex-col px-5 transition duration-700 sm:px-10 ${
          entered ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
        } ${active ? 'scale-[1.01]' : 'opacity-95'}`}
        style={{ transitionDelay: delay, ...safeAreaStyle }}
      >
        <div className="relative flex h-full flex-col overflow-hidden">
          <div className="relative flex flex-1 flex-col gap-6 overflow-y-auto">{children}</div>
        </div>
      </div>
    </section>
  );
}

type SectionBlockProps = {
  label: string;
  headline: string;
  badges: SectionBadge[];
  description?: string;
  kicker?: string;
  highlightText?: string;
  stats?: { completions: number; xpTotal: number; pillar: string | null; pillarStats: { xp: number; completions: number } | null };
  entered: boolean;
  index: number;
  active?: boolean;
  registerSectionRef?: (el: HTMLDivElement | null) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

function SectionBlock({
  label,
  headline,
  badges,
  description,
  kicker,
  highlightText,
  stats,
  entered,
  index,
  active,
  registerSectionRef,
  t
}: SectionBlockProps) {
  return (
    <SectionShell
      index={index}
      entered={entered}
      auraIndex={0}
      active={active}
      registerSectionRef={registerSectionRef}
    >
      <div className="flex h-full flex-col gap-6">
        <header className="space-y-4">
          <p className="text-[11px] uppercase tracking-[0.3em] text-emerald-100">{label}</p>
          <div className="space-y-3">
            <h2 className="text-4xl font-semibold leading-tight text-slate-50 drop-shadow-[0_0_28px_rgba(16,185,129,0.35)] sm:text-5xl">
              {headline}
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-50/90">
            {badges.map((badge) => (
              <span
                key={badge.id}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-semibold shadow-[0_0_0_1px_rgba(16,185,129,0.2),0_10px_30px_rgba(16,185,129,0.18)] ${
                  badge.active ? 'border border-emerald-300/40 bg-emerald-500/25 text-emerald-50' : 'border border-white/10 bg-white/5 text-slate-200'
                } ${badge.size === 'large' ? 'px-3.5 py-1.5 text-[11px]' : ''}`}
              >
                {badge.icon ? <span aria-hidden>{badge.icon}</span> : null}
                {badge.dotColor ? (
                  <span
                    className={`rounded-full ${badge.dotOnly ? 'h-4 w-4' : 'h-2.5 w-2.5'}`}
                    style={{ backgroundColor: badge.dotColor }}
                    aria-hidden
                  />
                ) : null}
                {badge.label ? <span>{badge.label}</span> : null}
              </span>
            ))}
          </div>
        </header>

          <div className="flex flex-1 flex-col justify-start gap-4 pt-2 text-lg leading-relaxed text-slate-100">
            {stats ? (
              <WeeklyKPIHighlight
                completions={stats.completions}
                xpTotal={stats.xpTotal}
                pillar={stats.pillar}
                pillarStats={stats.pillarStats}
                t={t}
              />
            ) : (
              <>
              {kicker ? <p className="text-sm uppercase tracking-[0.2em] text-emerald-100">{kicker}</p> : null}
              <p className="max-w-3xl text-lg text-slate-200 sm:text-xl">{description}</p>
              {highlightText ? (
                <p className="max-w-3xl text-lg font-semibold text-emerald-50 sm:text-xl">{highlightText}</p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </SectionShell>
  );
}

function WeeklyKPIHighlight({
  completions,
  xpTotal,
  pillar,
  pillarStats,
  t,
}: {
  completions: number;
  xpTotal: number;
  pillar: string | null;
  pillarStats: { xp: number; completions: number } | null;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const formatter = new Intl.NumberFormat('es-AR');
  const pillarLabel = pillar ?? t('feedback.weeklyWrapped.metrics.mixedMode');
  const pillarIcon = pillar ? getPillarIcon(pillar) : '🫀';
  const pillarNarrative =
    pillarLabel === t('feedback.weeklyWrapped.metrics.mixedMode')
      ? t('feedback.weeklyWrapped.metrics.pillarNarrativeMixed')
      : t('feedback.weeklyWrapped.metrics.pillarNarrative', { pillar: pillarLabel });
  const pillarStatsDetail = pillarStats
    ? t('feedback.weeklyWrapped.metrics.pillarStatsDetail', {
        xp: formatter.format(pillarStats.xp),
        completions: formatter.format(pillarStats.completions),
      })
    : null;

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/10 via-emerald-500/10 to-indigo-500/10 p-5 shadow-[0_20px_60px_rgba(8,47,73,0.42)] backdrop-blur">
        <div className="relative grid grid-cols-[72px_1fr] items-center gap-4 text-sm text-emerald-50 sm:grid-cols-[88px_1fr]">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-3xl sm:h-20 sm:w-20">
            <span aria-hidden>{pillarIcon}</span>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-100">{t('feedback.weeklyWrapped.metrics.dominantPillar')}</p>
            <p className="text-base font-semibold leading-snug text-slate-50 sm:text-lg">{pillarNarrative}</p>
            {pillarStatsDetail ? (
              <p className="text-sm text-emerald-50/90 sm:text-base">{pillarStatsDetail}</p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <KPIStat
          label={t('feedback.weeklyWrapped.metrics.completedTasks')}
          value={formatter.format(completions)}
          suffix={t('feedback.weeklyWrapped.metrics.tasksSuffix')}
          icon="🎯"
        />
        <KPIStat
          label={t('feedback.weeklyWrapped.metrics.experience')}
          value={formatter.format(xpTotal)}
          suffix={t('feedback.weeklyWrapped.metrics.experienceSuffix')}
          icon="🎖️"
        />
      </div>
    </div>
  );
}

function extractSummaryMetrics(
  text?: string | null,
): { completions: number; xpTotal: number } | null {
  if (!text) return null;

  const match = text.match(/Completaste\s+([\d\.]+)\s+tareas.*?sumaste\s+([\d\.]+)\s*GP/i);
  if (!match) return null;

  const completions = Number.parseInt(match[1]?.replace(/\./g, '') ?? '', 10);
  const xpTotal = Number.parseInt(match[2]?.replace(/\./g, '') ?? '', 10);

  if (!Number.isFinite(completions) || !Number.isFinite(xpTotal)) {
    return null;
  }

  return { completions, xpTotal };
}

function KPIStat({
  label,
  value,
  suffix,
  highlight = false,
  icon,
}: {
  label: string;
  value: string;
  suffix?: string;
  highlight?: boolean;
  icon?: string;
}) {
  const chipIcon = icon ?? (highlight ? '⚡️' : '✅');

  return (
    <div
      className={`group relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-white/10 via-emerald-500/10 to-indigo-500/10 p-4 shadow-[0_20px_60px_rgba(8,47,73,0.42)] backdrop-blur transition duration-500 ${
        highlight ? 'ring-1 ring-emerald-300/50 shadow-emerald-500/25' : ''
      }`}
    >
      <div className="relative grid grid-cols-[72px_1fr] items-center gap-3 sm:grid-cols-[88px_1fr]">
        <span
          className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-3xl sm:h-20 sm:w-20"
          aria-hidden
        >
          {chipIcon}
        </span>
        <div className="flex flex-1 flex-col gap-1 text-left">
          <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-100">{label}</p>
          <p className="text-4xl font-semibold leading-tight text-white sm:text-[44px]">
            {value}
            {suffix ? <span className="ml-2 text-base font-medium lowercase text-emerald-50">{suffix}</span> : null}
          </p>
        </div>
      </div>
    </div>
  );
}

export type HabitItem = {
  title: string;
  body: string;
  badge?: string;
  icon?: string;
  pillar?: string | null;
  daysActive?: number;
  weeksActive?: number | string;
  weeksSample?: number | string;
  completionRate?: number | string;
  weeklyGoal?: number | string | null;
  insightsTimeline?: TaskInsightsResponse['weeks']['timeline'];
};

type HabitsBlockProps = {
  title: string;
  description: string;
  items: HabitItem[];
  entered: boolean;
  startIndex: number;
  activeIndex: number;
  registerSectionRef: MutableRefObject<(HTMLDivElement | null)[]>;
  referenceDate?: Date;
  t: (key: string, params?: Record<string, string | number>) => string;
};

type HabitHealthLevel = ReturnType<typeof getHabitHealthFromInsights>['level'];

const HABIT_HEALTH_STYLES: Record<HabitHealthLevel, string> = {
  strong: 'bg-emerald-300 text-emerald-950',
  medium: 'bg-amber-300 text-amber-950',
  weak: 'bg-rose-300 text-rose-950',
};

function computeCompletionFromInsights(
  timeline: TaskInsightsResponse['weeks']['timeline'] | undefined,
  weeksSample: HabitItem['weeksSample'],
  completionRate: HabitItem['completionRate'],
  referenceDate?: Date,
):
  | {
      completionRatePct: number;
      weeksActive: number;
      weeksSample: number;
    }
  | null {
  if (!timeline || timeline.length === 0) {
    return null;
  }

  const completionSummary = computeWeeklyHabitHealth({
    timeline,
    weeksSample: Number(weeksSample),
    completionRate: Number(completionRate),
    referenceDate,
  });

  return {
    completionRatePct: completionSummary.completionPercent,
    weeksActive: completionSummary.completedWeeks,
    weeksSample: completionSummary.totalWeeks,
  };
}

export function resolveHabitHealth(
  { daysActive, weeksActive, weeksSample, completionRate, insightsTimeline }: HabitItem,
  referenceDate?: Date,
) {
  const parsedCompletionRate = Number(completionRate);
  const parsedWeeksSample = Number(weeksSample);
  const parsedWeeksActive = Number(weeksActive);
  const normalizedDays = Number.isFinite(daysActive) ? Math.max(0, Math.min(7, Math.round(daysActive ?? 0))) : 0;
  const weeklyDaysActive = normalizedDays;

  const normalizedWeeksActive = Number.isFinite(parsedWeeksActive)
    ? Math.max(0, Math.round(parsedWeeksActive))
    : normalizedDays > 0
      ? 1
      : 0;

  const normalizedWeeksSample = Number.isFinite(parsedWeeksSample)
    ? Math.max(0, Math.round(parsedWeeksSample))
    : normalizedWeeksActive > 0
      ? normalizedWeeksActive
      : normalizedDays > 0
        ? 1
        : 0;

  const normalizedCompletionRate = Number.isFinite(parsedCompletionRate)
    ? Math.max(0, Math.min(100, Math.round(parsedCompletionRate)))
    : null;

  const completionFromInsights = computeCompletionFromInsights(
    insightsTimeline,
    weeksSample,
    completionRate,
    referenceDate,
  );

  if (completionFromInsights) {
    return {
      ...getHabitHealthFromInsights(completionFromInsights.completionRatePct, completionFromInsights.weeksSample),
      completionRatePct: completionFromInsights.completionRatePct,
      weeksActive: completionFromInsights.weeksActive,
      weeksSample: completionFromInsights.weeksSample,
      weeklyDaysActive,
    };
  }

  const completionRateForHealth = (() => {
    if (normalizedCompletionRate !== null) {
      return normalizedCompletionRate;
    }

    if (normalizedWeeksSample > 0) {
      return Math.round((normalizedWeeksActive / Math.max(1, normalizedWeeksSample)) * 100);
    }

    return Math.round((normalizedDays / 7) * 100);
  })();

  const sampleForHealth = Math.max(0, normalizedWeeksSample, normalizedWeeksActive);

  return {
    ...getHabitHealthFromInsights(completionRateForHealth, sampleForHealth),
    completionRatePct: completionRateForHealth,
    weeksActive: normalizedWeeksActive,
    weeksSample: sampleForHealth,
    weeklyDaysActive,
  };
}

function HabitsBlock({ title, description, items, entered, startIndex, activeIndex, registerSectionRef, referenceDate, t }: HabitsBlockProps) {
  const slideLabel = t('feedback.weeklyWrapped.habits.slideLabel');
  const headline = t('feedback.weeklyWrapped.habits.headline');
  const subline = description || t('feedback.weeklyWrapped.habits.subline');
  return (
    <SectionShell
      index={startIndex}
      entered={entered}
      auraIndex={1}
      active={activeIndex === startIndex}
      registerSectionRef={(el) => (registerSectionRef.current[startIndex] = el)}
    >
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-100">{slideLabel}</p>
        <h3 className="text-2xl font-semibold text-slate-50 drop-shadow-[0_0_18px_rgba(56,189,248,0.3)]">{headline}</h3>
        <p className="text-xs leading-5 text-emerald-50/85">{subline}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {items.length > 0 ? (
          items.map((item, idx) => {
            const health = resolveHabitHealth(item, referenceDate);

            return (
              <div
                key={item.title}
                className="group rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 via-emerald-500/10 to-indigo-500/10 p-4 shadow-lg shadow-emerald-500/15 transition duration-700"
                style={{ transitionDelay: `${ANIMATION_DELAY * (startIndex + idx)}ms` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2 text-slate-50">
                    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-white/15 bg-white/10 px-2 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-100">
                      {getPillarIcon(item.pillar) || '🫀'}
                      <span className="sr-only">{item.pillar ?? '–'}</span>
                    </span>
                    <p className="text-sm font-semibold leading-snug">{item.title}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2 text-[11px] uppercase tracking-[0.14em]">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-emerald-300/30 bg-emerald-500/20 px-2 py-1 text-emerald-50 shadow-[0_0_0_1px_rgba(16,185,129,0.25)]">
                      🔥{health.weeklyDaysActive}/7
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[11px] text-emerald-50/80">
                    {t('feedback.weeklyWrapped.habits.goalMet', { weeksActive: health.weeksActive, weeksSample: health.weeksSample })}
                  </p>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-tight ${
                      HABIT_HEALTH_STYLES[health.level]
                    }`}
                  >
                    {health.label}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <p className="rounded-2xl border border-dashed border-white/15 bg-slate-900/70 p-4 text-sm text-slate-300">
            {t('feedback.weeklyWrapped.habits.empty')}
          </p>
        )}
      </div>
    </SectionShell>
  );
}

type LevelUpBlockProps = {
  levelUp: WeeklyWrappedPayload['levelUp'];
  entered: boolean;
  index: number;
  active?: boolean;
  registerSectionRef?: (el: HTMLDivElement | null) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

function LevelUpBlock({ levelUp, entered, index, active, registerSectionRef, t }: LevelUpBlockProps) {
  const levelLabel = levelUp.currentLevel ?? t('feedback.weeklyWrapped.levelUp.newLabel');
  const previousLabel = levelUp.previousLevel ?? Math.max(0, Number(levelLabel) - 1);

  return (
    <SectionShell
      index={index}
      entered={entered}
      auraIndex={2}
      active={active}
      registerSectionRef={registerSectionRef}
    >
      <div className="relative flex flex-1 overflow-hidden rounded-3xl border border-transparent bg-slate-950/70 p-[2px]">
        <div className="absolute inset-0 animate-pulse bg-[conic-gradient(at_30%_40%,#a855f7,#22d3ee,#22c55e,#f59e0b,#f472b6,#22d3ee)] opacity-70 blur" />
        <div className="relative flex h-full flex-col justify-between rounded-[26px] border border-white/10 bg-gradient-to-br from-slate-950/80 via-emerald-950/50 to-indigo-950/60 p-6 shadow-[0_20px_60px_rgba(34,197,94,0.2)] md:p-8">
          <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-emerald-400/20 blur-3xl" aria-hidden />
          <div className="absolute -left-10 -bottom-10 h-36 w-36 rounded-full bg-sky-400/15 blur-3xl" aria-hidden />
          <div className="flex h-full flex-col gap-5 text-slate-50 md:flex-row md:items-center md:justify-between md:gap-10">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.22em] text-emerald-100">{t('feedback.weeklyWrapped.levelUp.slideLabel')}</p>
              <h3 className="text-3xl font-semibold drop-shadow-[0_0_30px_rgba(16,185,129,0.45)]">{t('feedback.weeklyWrapped.levelUp.headline', { level: levelLabel })}</h3>
              <p className="max-w-2xl text-sm text-emerald-50/90">
                {levelUp.forced
                  ? t('feedback.weeklyWrapped.levelUp.mockCopy')
                  : t('feedback.weeklyWrapped.levelUp.realCopy')}
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 font-semibold uppercase tracking-[0.18em] text-emerald-50">
                  +{levelUp.xpGained.toLocaleString('es-AR')} {t('feedback.weeklyWrapped.levelUp.xpChip')}
                </span>
                <span className="rounded-full border border-white/15 bg-emerald-500/20 px-3 py-1 font-semibold uppercase tracking-[0.18em] text-emerald-50">
                  {previousLabel} → {levelLabel}
                </span>
              </div>
            </div>
            <div className="relative flex h-32 w-full max-w-xs items-center justify-center md:h-40">
              <div className="absolute h-28 w-28 animate-[ping_2.4s_ease-out_infinite] rounded-full bg-emerald-400/30" aria-hidden />
              <div className="absolute h-24 w-24 animate-[pulse_3s_ease-in-out_infinite] rounded-full bg-sky-400/20" aria-hidden />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-sky-400 text-center text-3xl font-bold leading-none text-slate-950 shadow-[0_0_40px_rgba(56,189,248,0.4)]">
                {levelLabel}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

type ProgressBlockProps = {
  pillar?: WeeklyWrappedSection;
  pillarDominant: string | null;
  completions?: number;
  xpTotal?: number;
  energyHighlight?: WeeklyWrappedPayload['summary']['energyHighlight'];
  effortBalance?: WeeklyWrappedPayload['summary']['effortBalance'];
  entered: boolean;
  index: number;
  active?: boolean;
  registerSectionRef?: (el: HTMLDivElement | null) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

function ProgressBlock({
  pillar,
  pillarDominant,
  completions = 0,
  xpTotal = 0,
  energyHighlight,
  effortBalance,
  entered,
  index,
  active,
  registerSectionRef,
  t,
}: ProgressBlockProps) {
  const slideLabel = t('feedback.weeklyWrapped.progress.slideLabel');
  const energySnapshot = computeEnergySnapshot(pillarDominant, completions, xpTotal, energyHighlight);
  const hasDelta = energySnapshot.hasHistory && typeof energySnapshot.delta === 'number';
  const energyHeadline = hasDelta
    ? t('feedback.weeklyWrapped.progress.deltaHeadline', {
        metric: energySnapshot.metric.label,
        delta: formatDeltaValue(energySnapshot.delta),
      })
    : energySnapshot.hasHistory
      ? t('feedback.weeklyWrapped.progress.energyHighlight', {
          metric: energySnapshot.metric.label,
          current: energySnapshot.current,
        })
      : t('feedback.weeklyWrapped.progress.energyNoData');
  const energyBarHeight = `${Math.max(12, Math.min(100, energySnapshot.current))}%`;
  const energyBarGradient = ENERGY_GRADIENT_BY_METRIC[energySnapshot.metric.label] ?? 'from-white/70 via-white/90 to-white';
  const energyCurrentLabel = `${Math.round(energySnapshot.current)}%`;
  const energyFocusStory = t('feedback.weeklyWrapped.progress.energyFocus.story', {
    area:
      energySnapshot.metric.label === 'HP'
        ? t('feedback.weeklyWrapped.progress.energyFocus.hp')
        : energySnapshot.metric.label === 'FOCUS'
          ? t('feedback.weeklyWrapped.progress.energyFocus.focus')
          : t('feedback.weeklyWrapped.progress.energyFocus.mood'),
  });
  const hasEffortBalance = Boolean((effortBalance?.total ?? 0) > 0);
  const balanceTotal = hasEffortBalance ? effortBalance?.total ?? 0 : 0;
  const easyPct = hasEffortBalance && balanceTotal ? Math.round((100 * (effortBalance?.easy ?? 0)) / balanceTotal) : 0;
  const mediumPct = hasEffortBalance && balanceTotal ? Math.round((100 * (effortBalance?.medium ?? 0)) / balanceTotal) : 0;
  const hardPct = hasEffortBalance && balanceTotal ? Math.max(0, 100 - easyPct - mediumPct) : 0;
  const balanceReading = !hasEffortBalance
    ? t('feedback.weeklyWrapped.progress.balanceNoData')
    : easyPct > 70
      ? t('feedback.weeklyWrapped.progress.balanceStability')
      : hardPct > 30
        ? t('feedback.weeklyWrapped.progress.balanceIntensity')
        : t('feedback.weeklyWrapped.progress.balanceBalanced');
  const hardInsight = !hasEffortBalance
    ? t('feedback.weeklyWrapped.progress.hardInsightNoData')
    : effortBalance?.topHardTask
      ? t('feedback.weeklyWrapped.progress.hardInsightTopTask', { task: effortBalance.topHardTask.title })
      : t('feedback.weeklyWrapped.progress.hardInsightEmpty');
  const insightDotColor = '#fbbf24';
  const effortInsights: { id: string; color: string; text: string }[] = [
    {
      id: 'balance',
      color: insightDotColor,
      text: balanceReading,
    },
    {
      id: 'hard-task',
      color: insightDotColor,
      text: hardInsight,
    },
  ];

  return (
    <SectionShell
      index={index}
      entered={entered}
      auraIndex={2}
      active={active}
      registerSectionRef={registerSectionRef}
    >
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-100">{slideLabel}</p>
        <h3 className="text-2xl font-semibold text-slate-50 drop-shadow-[0_0_18px_rgba(56,189,248,0.3)]">{t('feedback.weeklyWrapped.progress.headline')}</h3>
      </div>

      <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/80 via-slate-900/70 to-emerald-500/15 p-5 shadow-lg shadow-emerald-400/20">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-emerald-100">
          <span>{t('feedback.weeklyWrapped.progress.dailyEnergy')}</span>
        </div>
        <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 px-3 py-3">
          <div className="relative flex h-28 w-12 items-end overflow-hidden rounded-xl border border-white/20 bg-slate-900/60">
            <div className="absolute inset-x-0 top-1 text-center text-[11px] font-semibold text-emerald-50 drop-shadow-[0_0_8px_rgba(59,130,246,0.4)]">
              {energyCurrentLabel}
            </div>
            <div className={`w-full rounded-t-md bg-gradient-to-t ${energyBarGradient}`} style={{ height: energyBarHeight }} />
            <div className="absolute inset-0 animate-pulse bg-white/5" aria-hidden />
          </div>
          <div className="flex flex-col text-sm text-emerald-50">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-semibold">{energySnapshot.metric.label}</span>
              {hasDelta ? (
                <span className="text-xs font-semibold text-emerald-50">
                  <span className="font-bold text-emerald-300">{formatDeltaValue(energySnapshot.delta)}</span>{' '}
                  <span className="font-semibold">{t('feedback.weeklyWrapped.progress.vsPrevious7Days')}</span>
                </span>
              ) : (
                <span className="text-xs text-emerald-100">{energyHeadline}</span>
              )}
            </div>
            <span className="text-xs text-emerald-50/90">{energyFocusStory}</span>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/70 via-slate-900/60 to-emerald-500/10 p-5 shadow-lg shadow-emerald-500/15">
        <div className="space-y-3">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-100">{t('feedback.weeklyWrapped.progress.balanceTitle')}</p>
              <p className="text-lg font-semibold text-slate-50">{t('feedback.weeklyWrapped.progress.balanceSummary', { total: balanceTotal })}</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="h-5 w-full overflow-hidden rounded-full border border-white/10 bg-white/5">
              <div className="flex h-full w-full text-[11px] font-semibold text-white/90">
                <div
                  className="relative flex h-full items-center justify-center bg-gradient-to-r from-emerald-500/80 to-emerald-400/80"
                  style={{ width: `${easyPct}%` }}
                >
                  {easyPct > 4 ? <span className="drop-shadow-[0_0_6px_rgba(15,118,110,0.5)]">{easyPct}%</span> : null}
                </div>
                <div
                  className="relative flex h-full items-center justify-center bg-gradient-to-r from-cyan-500/70 to-sky-400/80"
                  style={{ width: `${mediumPct}%` }}
                >
                  {mediumPct > 4 ? <span className="drop-shadow-[0_0_6px_rgba(14,165,233,0.5)]">{mediumPct}%</span> : null}
                </div>
                <div
                  className="relative flex h-full items-center justify-center bg-gradient-to-r from-rose-500/80 to-orange-400/80"
                  style={{ width: `${hardPct}%` }}
                >
                  {hardPct > 4 ? <span className="drop-shadow-[0_0_6px_rgba(190,24,93,0.45)]">{hardPct}%</span> : null}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 text-[12px] text-emerald-50/80">
              <span className="flex items-center gap-2 rounded-full bg-white/0 px-2 py-1">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" aria-hidden />
                <span className="font-medium">{t('feedback.weeklyWrapped.progress.easy')}</span>
              </span>
              <span className="flex items-center gap-2 rounded-full bg-white/0 px-2 py-1">
                <span className="h-2.5 w-2.5 rounded-full bg-cyan-400" aria-hidden />
                <span className="font-medium">{t('feedback.weeklyWrapped.progress.medium')}</span>
              </span>
              <span className="flex items-center gap-2 rounded-full bg-white/0 px-2 py-1">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-400" aria-hidden />
                <span className="font-medium">{t('feedback.weeklyWrapped.progress.hard')}</span>
              </span>
            </div>
          </div>

          <ul className="space-y-2 text-sm text-emerald-50">
            {effortInsights.map((insight) => (
              <li
                key={insight.id}
                className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-3 text-slate-100 shadow-inner shadow-emerald-400/5"
              >
                <span
                  className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: insight.color }}
                  aria-hidden
                />
                <span className="leading-relaxed">{insight.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SectionShell>
  );
}

type EmotionHighlightBlockProps = {
  emotionHighlight: WeeklyWrappedPayload['emotions'];
  entered: boolean;
  index: number;
  active?: boolean;
  registerSectionRef?: (el: HTMLDivElement | null) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  language: 'es' | 'en';
};

function EmotionHighlightBlock({ emotionHighlight, entered, index, active, registerSectionRef, t, language }: EmotionHighlightBlockProps) {
  const weeklyEmotion = emotionHighlight.weekly;
  const biweeklyEmotion = emotionHighlight.biweekly ?? weeklyEmotion;
  const weeklyColor = weeklyEmotion?.color ?? '#fbbf24';
  const biweeklyColor = biweeklyEmotion?.color ?? weeklyColor;
  const weeklyCopy = resolveEmotionCopy(language, weeklyEmotion?.key ?? null);
  const biweeklyCopy = resolveEmotionCopy(language, biweeklyEmotion?.key ?? null);
  const weeklyLabel = weeklyEmotion ? weeklyCopy.label : t('feedback.weeklyWrapped.emotion.noDominant');
  const weeklyMessage = weeklyEmotion ? weeklyCopy.weekly_message : t('feedback.weeklyWrapped.emotion.weeklyMessageFallback');
  const biweeklyLabel = biweeklyEmotion ? biweeklyCopy.label : t('feedback.weeklyWrapped.emotion.biweeklyFallback');
  const biweeklyContext = biweeklyEmotion
    ? biweeklyCopy.biweekly_context
    : t('feedback.weeklyWrapped.emotion.biweeklyContextFallback');
  const slideLabel = t('feedback.weeklyWrapped.emotion.slideLabel');

  return (
    <SectionShell
      index={index}
      entered={entered}
      auraIndex={1}
      active={active}
      registerSectionRef={registerSectionRef}
    >
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-100">{slideLabel}</p>
        <h3 className="text-2xl font-semibold text-slate-50 drop-shadow-[0_0_18px_rgba(251,191,36,0.35)]">{t('feedback.weeklyWrapped.emotion.headline')}</h3>
        <p className="text-sm text-emerald-50">{t('feedback.weeklyWrapped.emotion.description')}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-4 rounded-3xl border border-white/10 bg-gradient-to-br from-amber-200/15 via-amber-400/10 to-rose-400/15 p-5 shadow-lg shadow-amber-300/25">
          <div className="flex items-center gap-3">
            <span
              className="flex h-16 w-16 items-center justify-center rounded-full text-2xl font-semibold"
              style={{ backgroundColor: `${weeklyColor}33`, color: weeklyColor }}
              aria-hidden
            >
              ●
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-amber-50">{t('feedback.weeklyWrapped.emotion.dominant7Days')}</p>
              <p className="text-2xl font-semibold text-slate-900 drop-shadow-[0_0_18px_rgba(251,191,36,0.35)]">{weeklyLabel}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/80 p-3 text-sm text-amber-900 shadow-inner shadow-amber-500/10">
            {weeklyMessage}
          </div>
        </div>

        <div className="space-y-4 rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/80 via-slate-900/70 to-emerald-500/15 p-5 shadow-lg shadow-emerald-400/20">
          <div className="flex items-center gap-3">
            <span
              className="flex h-16 w-16 items-center justify-center rounded-full text-2xl font-semibold"
              style={{ backgroundColor: `${biweeklyColor}33`, color: biweeklyColor }}
              aria-hidden
            >
              ●
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-100">{t('feedback.weeklyWrapped.emotion.trend15Days')}</p>
              <p className="text-2xl font-semibold text-slate-50">{biweeklyLabel}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-3 text-sm text-slate-50 shadow-inner shadow-emerald-400/10">
            {biweeklyEmotion
              ? t('feedback.weeklyWrapped.emotion.biweeklyTrend', { label: biweeklyLabel.toLowerCase() })
              : biweeklyContext}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

type ClosingBlockProps = {
  message: string;
  accent: string;
  onComplete: () => void;
  onViewRewards: () => void;
  entered: boolean;
  index: number;
  active?: boolean;
  registerSectionRef?: (el: HTMLDivElement | null) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

function ClosingBlock({ message, accent, onComplete, onViewRewards, entered, index, active, registerSectionRef, t }: ClosingBlockProps) {
  const slideLabel = t('feedback.weeklyWrapped.closing.slideLabel');
  return (
    <SectionShell
      index={index}
      entered={entered}
      auraIndex={0}
      active={active}
      registerSectionRef={registerSectionRef}
    >
      <div className="flex h-full flex-col items-center justify-center gap-10 text-center text-slate-50">
        <div className="space-y-4">
          <p className="text-[11px] uppercase tracking-[0.25em] text-emerald-100">{slideLabel}</p>
          <h3 className="text-4xl font-semibold leading-tight drop-shadow-[0_0_28px_rgba(16,185,129,0.35)] sm:text-5xl">{accent}</h3>
          <p className="max-w-3xl text-xl leading-relaxed text-slate-100 sm:text-[22px]">{message}</p>
        </div>

        <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
          <button
            type="button"
            onClick={onViewRewards}
            className="w-full min-w-[220px] rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 px-6 py-3 text-base font-semibold text-slate-950 shadow-lg shadow-emerald-500/30 transition hover:-translate-y-0.5 sm:w-auto"
          >
            {t('feedback.weeklyWrapped.closing.viewAchievements')}
          </button>
          <button
            type="button"
            onClick={onComplete}
            className="w-full min-w-[220px] rounded-full border border-white/25 bg-white/10 px-6 py-3 text-base font-semibold text-emerald-50 transition hover:-translate-y-0.5 hover:border-emerald-300/50 hover:bg-emerald-400/20 hover:text-slate-950 sm:w-auto"
          >
            {t('feedback.common.close')}
          </button>
        </div>
      </div>
    </SectionShell>
  );
}

function formatRange(range: WeeklyWrappedPayload['weekRange'], language: 'es' | 'en'): string {
  const start = new Date(range.start);
  const end = new Date(range.end);
  return `${start.toLocaleDateString(language === 'es' ? 'es-AR' : 'en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(language === 'es' ? 'es-AR' : 'en-US', { month: 'short', day: 'numeric' })}`;
}

function getHabitIcon(pillar?: string | null, index = 0): string {
  if (!pillar) return HABIT_ICON_SETS.Body[index % HABIT_ICON_SETS.Body.length];
  const set = HABIT_ICON_SETS[pillar];
  if (!set || set.length === 0) {
    return HABIT_ICON_SETS.Body[index % HABIT_ICON_SETS.Body.length];
  }
  return set[index % set.length];
}

function getPillarIcon(pillar?: string | null): string {
  const normalized = normalizePillarCode(pillar);
  if (!normalized) return '';
  return PILLAR_ICONS[normalized] ?? '';
}

function formatDeltaValue(delta: number | null): string {
  if (delta === null || Number.isNaN(delta)) return '';
  const rounded = Math.round(delta * 10) / 10;
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
}

function computeEnergySnapshot(
  pillarDominant: string | null,
  completions: number,
  xpTotal: number,
  energyHighlight?: WeeklyWrappedPayload['summary']['energyHighlight'],
) {
  const metric = energyHighlight
    ? Object.values(ENERGY_METRIC_BY_PILLAR).find((entry) => entry.label === energyHighlight.metric) ??
      ENERGY_METRIC_BY_PILLAR.Body
    : pillarDominant && ENERGY_METRIC_BY_PILLAR[pillarDominant]
      ? ENERGY_METRIC_BY_PILLAR[pillarDominant]
      : ENERGY_METRIC_BY_PILLAR.Body;
  const normalizedCompletionScore = Math.min(100, Math.round((completions / 14) * 100));
  const xpScore = Math.min(100, Math.round(xpTotal / 10));
  const current = energyHighlight?.value ?? Math.max(12, normalizedCompletionScore || xpScore || 0);
  const hasHistory = Boolean(energyHighlight?.hasHistory);
  const delta = hasHistory && typeof energyHighlight?.deltaPct === 'number'
    ? Math.round(energyHighlight.deltaPct * 10) / 10
    : null;

  return {
    metric,
    delta,
    current,
    hasHistory,
  };
}

// Nota de prueba: navegar next/prev entre slides y el fondo sigue animado siempre.
